import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Button, CircularProgress, Divider, Fade, Paper, Popper, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { pdf } from '@react-pdf/renderer';
import saveAs from 'file-saver';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { LabelKeyObject } from 'react-csv/lib/core';
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import { ReactComponent as ShareIcon } from "../../../assets/icons/share.svg";
import { AuthContext } from '../../../contexts/AuthContext';
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { ExportableFields } from '../../../contexts/DashboardsContext/DashboardsProvider';
import { FileStructureContext } from '../../../contexts/FileStructureContext';
import { DashboardsTableContext } from '../../../contexts/TablesContext/DashboardsTableProvider';
import WorkspacesTableProvider from '../../../contexts/TablesContext/WorkspacesTableProvider';
import { WorkspaceContext } from "../../../contexts/WorkspaceContext";
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import useBulkDashboards from '../../../hooks/useBulkDashboards';
import useDashboards from '../../../hooks/useDashboards';
import { getGroupDashboardsFunc, removeWorkspaceFromDashboardFunc, updateDashboardFunc } from "../../../lib/helper";
import theme from '../../../theme';
import { CompanyFile, DashboardQueriesType } from '../../../types/files';
import DealOwnerMenu from '../../atoms/DealOwnerMenu';
import InvestorsIconFC from "../../atoms/InvestorsIconFC";
import ConfirmDialog from '../../modals/ConfirmDialog';
import AddCompaniesToSharedWorkspaceModal from '../../modals/multi-action/AddCompaniesToSharedWorkspaceModal';
import FindInvestorsShareDealsLoaderModal from "../../modals/multi-action/FindInvestorsShareDealsLoaderModal";
import { collectionKey } from '../../organisms/dashboards/DashboardsView';
import ExportDocument from '../dashboard-export/ExportDocument';
import CompaniesStatusMenu from './CompaniesStatusMenu';

const edjsParser = require("editorjs-parser").default;
const parser = new edjsParser();

const useStyles = makeStyles((theme) => ({
    popup: {
        zIndex: 1300,
    },
    modal: {
        minWidth: 270,
        width: '100%',
        height: 'fit-content',
        padding: '12px 8px 0px 8px',
        border: '1px solid lightgray',
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: 'black',
        marginLeft: 8,
    },
    button: {
        width: '100%',
        padding: '6px 8px !important',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: 'black',
        textTransform: 'none',
        justifyContent: 'flex-start',
        "& > .MuiButton-startIcon": {
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                color: theme.palette.primary.light,
            }
        },
    },
    expandButton: {
        minWidth: '20px',
        padding: 0,
        '&:hover': {
            backgroundColor: 'transparent'
        }
    },
    expandMoreIcon: {
        color: theme.colors.neutral['500'],
        width: '20px',
        height: '20px',
        transition: '0.25s ease-in-out ',
        '&:hover': {
            color: theme.colors.neutral['700'],
        },
    },
    donwloadIcon: {
        width: 24,
        height: 24,
        stroke: theme.palette.primary.light,
    },
    syncExternally: {
        width: '24ch',
        height: 38,
        borderRadius: 32,
        color: theme.palette.primary.main,
        borderWidth: 2,
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
    },
    synced: {
        width: '24ch',
        height: 38,
        padding: 'unset',
        borderRadius: 32,
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
        "&.Mui-disabled": {
            color: '#fff',
            background: '#8aba71',
            fontFamily: 'Lato',
            fontSize: '1rem',
            fontWeight: 'bold',
            textTransform: 'none',
        },
    },
}));

const ExcludedHeaders = ['Communications', 'LinkedIn Posts', '[1]', '[2]'];

const CompaniesMultiAction = React.forwardRef<any>((props, ref) => {
    const classes = useStyles();
    const { userGroup: author } = useContext(AuthContext);
    const { fileStructure } = useContext(FileStructureContext);
    const { mappedOverviewQueries, setDashboards } = useContext(DashboardsContext);
    const { headers, setFilters } = useContext(DashboardsTableContext);
    const { workspace } = useContext(WorkspaceContext);
    const { buildExportable, assignAssociatedDashboards, getBulkDashboards, clearBulk, addToBulk } = useBulkDashboards();
    const { deleteDashboard } = useDashboards();
    const { dashboardOwner, dashboardSource } = useBackwardsCompatible();

    const [loading, setLoading] = useState<string>('');
    const [csvHeaders, setCSVHeaders] = useState<LabelKeyObject[]>([]);
    const [csvData, setCSVData] = useState<object[]>([]);
    const [shareDealsModalOpen, setShareDealsModalOpen] = useState<boolean>(false);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState<boolean>(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
    const [addToSharedWorkspaceModalOpen, setAddCompaniesToSharedWorkspaceModalOpen] = useState<boolean>(false);
    const [dealOwnerPopupOpen, setDealOwnerPopupOpen] = useState<HTMLElement|null>(null);
    const anchorRef = useRef<any>(ref);

    const { actions, right } = props as { actions?: string[], right?: number };
    const { top } = anchorRef?.current?.current?.getBoundingClientRect() || { top: 0 };

    // const documentName = `Notissia Dashboard Export ${moment().format('(M-D-YYYY)')}`;

    const showAction = (action: string) => actions?.join(';').includes(action);

    const bulkDashboards = useMemo(() => getBulkDashboards(collectionKey), [getBulkDashboards]);

    const allSynced = useMemo(() => bulkDashboards.every(({dashboard}) => dashboard.shouldSyncExternally), [bulkDashboards]);

    const getQueryAnswer = useCallback((dashboardId: string, title: string) => (
        mappedOverviewQueries.get(`${dashboardId}:${title}`)
    ), [mappedOverviewQueries]);

    const handleSync = useCallback(() => {
        setLoading('sync');
        Promise.all(bulkDashboards
            .filter(({dashboard}) => !dashboard.shouldSyncExternally)
            .map(async ({dashboard}) => {
                const updatedDashboard = { ...dashboard, shouldSyncExternally: true };
                return await updateDashboardFunc(updatedDashboard);
            })
        ).then(() => {
            setLoading('');

            const newBulkDashboards: DashboardQueriesType[]  = [];
            setDashboards(prev => prev.map(d => {
                const updated = bulkDashboards.find(b => b.dashboard.id === d.id);
                if (updated)
                    newBulkDashboards.push({ ...updated, dashboard: { ...d, shouldSyncExternally: true } });
                return updated ? ({ ...d, ...updated.dashboard, shouldSyncExternally: true }) : d;
            }));

            const timeOut = setTimeout(() => {
                clearTimeout(timeOut);
                newBulkDashboards.forEach(({dashboard, queries}) => {
                    addToBulk(collectionKey, dashboard, queries);
                });
            }, 1000);
        });
    }, [bulkDashboards, addToBulk, setDashboards]);

    const handleAssigntoWorkspace = useCallback(() => {
        setLoading('assign');
        assignAssociatedDashboards().finally(() => {
            setLoading('');
        });
    }, [assignAssociatedDashboards]);

    const handleExportPDF = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        setLoading('export-pdf');
        buildExportable(collectionKey, ExportableFields)
            .then((bulkDashboards: DashboardQueriesType[]) => bulkDashboards.forEach((bulkDashboard) =>
                pdf(
                    <ExportDocument bulkDashboards={[bulkDashboard]} author={author} />
                ).toBlob().then(blob => saveAs(blob, `${bulkDashboard.dashboard.title}.pdf`))
            )).finally(() => setLoading(''));
    }, [author, buildExportable]);

    const handleExportCSV = useCallback(() => {
        setLoading('export-csv');
        setCSVHeaders((headers ?? [])
            .filter(header => !ExcludedHeaders.includes(header))
            .map(header => ({
                label: header,
                key: header.replace(/\s+/g, '-').toLowerCase(),
            })));
        setCSVData(bulkDashboards.map(({dashboard}) => {
            const location = getQueryAnswer(dashboard.id, 'Location') as string;

            const keyPeople = getQueryAnswer(dashboard.id, 'Key People');
            const keyPeopleList = Array.isArray(keyPeople) ? keyPeople.reduce((acc, person) => {
                if (person?.title)
                    acc.push(person.title?.split('|')?.[0]?.split('-')?.[0]);
                return acc;
            }, [] as string[]).join(', ') : '';

            const files = fileStructure.filter(file => !file.isDirectory && file.dashboardId === dashboard?.id) as CompanyFile[];
            const defaultFile = files.find(file => file.isDeck) ?? files[0];

            const commentary = dashboard?.notes?.find(note => note.title === `$['Commentary']`);
            const commentaryNote = new DOMParser().parseFromString(parser.parse({ blocks: (JSON.parse(commentary?.data || '[]') as any[]).slice(1) }), 'text/html')?.body?.textContent?.trim() || '';

            const owner = dashboardOwner(dashboard);
            const source = dashboardSource(dashboard);

            return ({
                'name': dashboard.title,
                'status': dashboard.status.charAt(0).toUpperCase() + dashboard.status.slice(1).toLowerCase(),
                'stage': dashboard.investmentStage,
                ...Object.fromEntries((dashboard.tagGroups ?? []).map(({ title, tags }) =>
                    [title.replace(/\s+/g, '-').toLowerCase(), (tags ?? []).join(', ')])),
                'key-people': keyPeopleList || '',
                'deck/teaser': defaultFile?.name || '',
                'location': (location !== '' && location !== 'N/A') ? location : '',
                'owner': `${owner?.name || ''}${owner?.email ? `${owner?.name ? ` <${owner?.email}>` : owner?.email}` : ''}`,
                'source': `${source?.name || ''}${source?.email ? `${source?.name ? ` <${source?.email}> ` : source?.email}` : ''}`,
                'commentary': commentaryNote || '',
                'updated': dashboard.updatedAt,
                'added': dashboard.createdAt,
            });
        }));
        setLoading('');
    // eslint-disable-next-line
    }, [headers, bulkDashboards, fileStructure, getQueryAnswer]);

    const handleRemovefromWorkspace = useCallback(() => {
        const promises: Promise<boolean>[] = [];
        const bulkDashboards = getBulkDashboards(collectionKey);

        setLoading('remove');
        bulkDashboards.forEach(({dashboard}) => promises.push(removeWorkspaceFromDashboardFunc({
            dashboardId: dashboard.id, workspaceId: workspace!.id, group: author
        })));
        Promise.all(promises)
            .then(() => Promise.resolve(getGroupDashboardsFunc(author)))
            .finally(() => {
                setLoading('');
                setConfirmRemoveOpen(false);
            });
    }, [author, getBulkDashboards, workspace]);

    const handleDelete = useCallback(() => {
        const promises: Promise<boolean>[] = [];
        const bulkDashboards = getBulkDashboards(collectionKey);

        setLoading('delete');
        bulkDashboards.forEach(({dashboard}) => promises.push(deleteDashboard(dashboard.id)));
        Promise.all(promises).then(() => {
            const bulkDashboardIds = bulkDashboards.map(({dashboard}) => dashboard.id);

            setDashboards(prev => prev.filter(dashboard => !bulkDashboardIds.includes(dashboard.id)))
            clearBulk(collectionKey);
        }).finally(() => {
            setLoading('');
            setConfirmDeleteOpen(false);
        });
    // eslint-disable-next-line
    }, [getBulkDashboards]);

    useEffect(() => {
        setLoading('');

        return () => {
            clearBulk(collectionKey);
        };
    // eslint-disable-next-line
    }, []);

    return (<>
        <Popper open={!!bulkDashboards.length} className={classes.popup} transition
            sx={{ top: `${top + 50}px !important`, left: `calc(100% - 280px - ${right}px) !important` }}>
            {({ TransitionProps }) => (
                <Fade {...TransitionProps}>
                    <Paper className={classes.modal} elevation={3} square={false}>
                        <Stack direction="column" spacing={1} alignItems="flex-start" justifyContent="center" width="100%" mb={1}>
                            <Typography className={classes.title}>{`${bulkDashboards.length} compan${bulkDashboards.length > 1 ? 'ies' : 'y'} selected`}</Typography>
                            <Divider sx={{ width: '100%' }}/>
                            <Stack alignItems="center" justifyContent="center" width="100%" pt={1}>
                                {!allSynced ? (
                                    <Button variant="outlined"
                                        className={classes.syncExternally}
                                        startIcon={(loading === 'sync') ? (
                                            <CircularProgress size={24} sx={{ color: theme.palette.primary.light }} />
                                        ): (<CachedIcon sx={{ fontSize: "28px !important", color: theme.colors.cyan['500'] }} />)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleSync();
                                        }} disableRipple>{'Sync externally'} </Button>
                                ) : (
                                    <Button variant="contained"
                                        className={classes.synced}
                                        startIcon={<CheckCircleIcon sx={{ fontSize: "28px !important", color: '#327d1a', fill: '#fff' }} />}
                                        disabled> {'Sync complete'} </Button>
                                )}
                            </Stack>
                            <Stack direction="column" alignItems="flex-start" justifyContent="center" width="100%">
                                {showAction('Find investors & share') && (
                                    <Button className={classes.button}
                                        startIcon={<ShareIcon width={24} height={24} stroke={theme.palette.primary.light} />}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setShareDealsModalOpen(true);
                                        }}> {'Find investors & share'} </Button>
                                )}
                                {showAction('Move to') && (
                                    <Stack direction="row" alignItems="center" justifyContent="flex-start" px="4px !important" width="100%">
                                        <ArrowForwardIcon sx={{ width: 24, height: 24, color: theme.palette.primary.light }} />
                                        <Typography className={classes.button} sx={{ minWidth: '80px !important'}}> {'Update status to: '} </Typography>
                                        <CompaniesStatusMenu />
                                    </Stack>
                                )}
                                {showAction('Add to shared workspace') && (
                                    <Button className={classes.button}
                                        startIcon={<InvestorsIconFC width={24} height={24} color={ theme.palette.primary.light } />}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setAddCompaniesToSharedWorkspaceModalOpen(true);
                                        }}
                                    > {'Add to shared workspace'} </Button>
                                )}
                                {showAction('Update deal owner') && (
                                    <Stack direction="row" alignItems="center" justifyContent="flex-start" px="4px !important" width="100%">
                                        <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, color: theme.palette.primary.light }} />
                                        <Typography className={classes.button} sx={{ minWidth: '80px !important'}}> {'Update deal owner '} </Typography>
                                        <Button className={classes.expandButton} onClick={(e) => setDealOwnerPopupOpen(e.currentTarget)} size="small">
                                            <ExpandMoreIcon className={classes.expandMoreIcon} />
                                        </Button>
                                    </Stack>
                                )}
                                {showAction('Generate one-pager (PDF)') && (
                                    <Button className={classes.button}
                                        startIcon={(loading === 'export-pdf') ? (
                                            <CircularProgress size="24px" sx={{ color: theme.palette.primary.light }} />
                                        ): (<IosShareRoundedIcon />)}
                                        onClick={handleExportPDF}
                                    > {'Generate one-pager (PDF)'} </Button>
                                )}
                                {showAction('Export to CSV') && (
                                    <CSVLink uFEFF={false}
                                        headers={csvHeaders}
                                        data={csvData}
                                        filename={`list-of-companies.csv`}
                                        onClick={handleExportCSV}
                                        style={{ width: '100%' }}>
                                        <Button className={classes.button}
                                            startIcon={(loading === 'export-csv') ? (
                                                <CircularProgress size="24px" sx={{ color: theme.palette.primary.light }} />
                                            ): (<DownloadIcon className={classes.donwloadIcon} />)}
                                        > {'Export to CSV'} </Button>
                                    </CSVLink>
                                )}
                                {showAction('Remove from workspace') && (
                                    <Button variant="text"
                                        className={classes.button}
                                        startIcon={<DeleteOutlineIcon
                                            sx={{ color: `${theme.colors.error['500']} !important` }} />}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setConfirmRemoveOpen(true);
                                        }}
                                        sx={{ color: `${theme.colors.error['500']} !important` }}
                                    > {'Remove from workspace'} </Button>
                                )}
                                {showAction('Delete') && (
                                    <Button variant="text"
                                        className={classes.button}
                                        startIcon={<DeleteOutlineIcon
                                            sx={{ color: `${theme.colors.error['500']} !important` }} />}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setConfirmDeleteOpen(true);
                                        }}
                                        sx={{ color: `${theme.colors.error['500']} !important` }}
                                    > {'Delete'} </Button>
                                )}
                                {!!actions?.length && (
                                    <Divider sx={{ width: '100%', margin: '4px 0 !important' }}/>
                                )}
                                <Button className={classes.button}
                                    startIcon={<ClearIcon />}
                                    onClick={(e: any) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        clearBulk(collectionKey);
                                        setFilters?.({ added: [] });
                                    }}
                                    sx={{ color: `${theme.palette.primary.light} !important` }}
                                > {'Clear selection'} </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Fade>
            )}
        </Popper>
        {shareDealsModalOpen && (
            <FindInvestorsShareDealsLoaderModal
                isOpen={shareDealsModalOpen}
                onClose={() => setShareDealsModalOpen(false)} />
        )}
        {addToSharedWorkspaceModalOpen && (
            <WorkspacesTableProvider>
                <AddCompaniesToSharedWorkspaceModal
                    isOpen={addToSharedWorkspaceModalOpen}
                    loading={loading === 'assign'}
                    onAdd={handleAssigntoWorkspace}
                    onClose={() => setAddCompaniesToSharedWorkspaceModalOpen(false)} />
            </WorkspacesTableProvider>
        )}
        {dealOwnerPopupOpen && (
            <DealOwnerMenu
                anchorElement={dealOwnerPopupOpen}
                onClose={() => setDealOwnerPopupOpen(null)} />
        )}
        <ConfirmDialog
            title="Remove from workspace confirmation"
            content="Are you sure you want to remove the selected companies from its workspace?"
            open={confirmRemoveOpen}
            loading={loading === 'remove'}
            confirmLabel="Remove"
            confirmCallback={handleRemovefromWorkspace}
            cancelCallback={() => setConfirmRemoveOpen(false)}
        />
        <ConfirmDialog
            title="Delete confirmation"
            content="Are you sure you want to delete the selected companies?"
            open={confirmDeleteOpen}
            loading={loading === 'delete'}
            confirmCallback={handleDelete}
            cancelCallback={() => setConfirmDeleteOpen(false)}
        />
    </>);
});

export default CompaniesMultiAction;
